import { useMemo } from 'react';
import type { Goal } from '@/types/goal';
import { format } from 'date-fns';

export interface InlineHint {
  habitId: string;
  message: string;
  type: 'streak' | 'missed' | 'stalled_goal';
}

/**
 * Generate client-side heuristic hints for habit cards.
 * No LLM calls — purely deterministic from existing data.
 */
export function useInlineHints(goals: Goal[]): Map<string, InlineHint> {
  return useMemo(() => {
    const hints = new Map<string, InlineHint>();
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = format(yesterday, 'yyyy-MM-dd');

    for (const goal of goals) {
      // Check if goal is stalled (no progress change in 14+ days, under 100%)
      const goalStalled =
        goal.progress < 100 &&
        goal.createdAt &&
        today.getTime() - new Date(goal.createdAt).getTime() > 14 * 24 * 60 * 60 * 1000;

      for (const habit of goal.habits) {
        // 7+ day streak
        if (habit.streak >= 7) {
          hints.set(habit.id, {
            habitId: habit.id,
            message: 'Great consistency! Keep it up.',
            type: 'streak',
          });
          continue;
        }

        // Missed yesterday (had some history but missed yesterday)
        if (
          habit.completedDates.length > 0 &&
          !habit.completedDates.includes(yesterdayStr) &&
          habit.streak === 0
        ) {
          hints.set(habit.id, {
            habitId: habit.id,
            message: "Missed yesterday \u2014 today's a fresh start.",
            type: 'missed',
          });
          continue;
        }

        // Linked to stalled goal
        if (goalStalled) {
          hints.set(habit.id, {
            habitId: habit.id,
            message: `This habit supports "${goal.title}" \u2014 every day counts.`,
            type: 'stalled_goal',
          });
        }
      }
    }

    return hints;
  }, [goals]);
}
